// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { ChevronLeft, ChevronRight, FileText, LogOut, Moon, Settings, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useAuthStore } from '@/app/stores/auth-store';
import { useLogout } from '@/app/features/auth';
import { cn } from '@/lib/utils';
import { NotificationBell } from './notification-bell';
import { SidebarNav } from './sidebar-nav';

interface AppSidebarProps {
  /** Mobile: completely hidden (w-0). Desktop minimised: icon-only rail. */
  collapsed?: boolean;
  /** Desktop-only minimised state (icon rail). */
  minimised?: boolean;
  onToggle?: () => void;
  /** Toggle between expanded and minimised on desktop. */
  onToggleMinimise?: () => void;
}

/**
 * Enterprise floating glassmorphism sidebar — identical visual treatment to OSS,
 * with added credit balance badge. Uses same base components and design tokens.
 * Supports a minimised icon-rail mode on desktop.
 */
export function AppSidebar({ collapsed, minimised, onToggle, onToggleMinimise }: AppSidebarProps) {
  const { user } = useAuthStore();
  const logout = useLogout();
  const location = useLocation();
  const isSettingsActive =
    location.pathname.startsWith('/settings/') &&
    !location.pathname.startsWith('/settings/templates');
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('flaircv-theme');
    if (stored === 'dark' || (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
      setIsDark(true);
    }
  }, []);

  function toggleTheme() {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('flaircv-theme', next ? 'dark' : 'light');
  }

  return (
    <aside
      className={cn(
        'flex h-full flex-col rounded-lg border border-sidebar-border bg-sidebar-background glass transition-[width] duration-200',
        collapsed
          ? 'w-0 overflow-hidden border-0'
          : minimised
            ? 'w-[52px]'
            : 'w-[260px] lg:w-[272px]',
      )}
      aria-label="Application sidebar"
    >
      {/* Logo */}
      <div
        className={cn(
          'flex h-12 lg:h-14 items-center border-b border-sidebar-border/50',
          minimised ? 'justify-center px-0' : 'gap-2.5 px-4',
        )}
      >
        <div className="flex h-7 w-7 lg:h-8 lg:w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FileText className="h-4 w-4 lg:h-[18px] lg:w-[18px] text-primary" />
        </div>
        {!minimised && (
          <span className="text-[15px] lg:text-[17px] font-semibold tracking-tight text-sidebar-foreground">
            FlairCV
          </span>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-3 lg:py-4">
        <SidebarNav collapsed={minimised} />
      </div>

      {/* Footer: settings link, credit badge, theme toggle, user, collapse button */}
      <div className="border-t border-sidebar-border/50 p-3">
        {/* Settings link */}
        {minimised ? (
          <Link
            to="/settings/ai-provider"
            title="Settings"
            className={cn(
              'group relative mb-2 flex items-center justify-center rounded-lg p-2 transition-all duration-150',
              isSettingsActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            )}
            aria-current={isSettingsActive ? 'page' : undefined}
          >
            <Settings className={cn('h-[18px] w-[18px] shrink-0', isSettingsActive ? 'text-primary' : 'text-sidebar-foreground/80')} />
            <span className="pointer-events-none absolute left-full ml-2 z-50 hidden whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[12px] font-medium text-background shadow-md group-hover:block">
              Settings
            </span>
          </Link>
        ) : (
          <Link
            to="/settings/ai-provider"
            className={cn(
              'mb-2 flex items-center gap-3 rounded-lg border-l-2 px-3 py-2 lg:py-2.5 text-[14px] lg:text-[15px] font-medium transition-all duration-150',
              isSettingsActive
                ? 'border-primary bg-sidebar-accent text-sidebar-accent-foreground'
                : 'border-transparent text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            )}
            aria-current={isSettingsActive ? 'page' : undefined}
          >
            <Settings className={cn('h-4 w-4 lg:h-[18px] lg:w-[18px] shrink-0', isSettingsActive ? 'text-primary' : 'text-sidebar-foreground/80')} />
            <span>Settings</span>
          </Link>
        )}

        {/* Theme toggle */}
        {minimised ? (
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={toggleTheme}
            title={isDark ? 'Light mode' : 'Dark mode'}
            className="group relative mb-2 flex w-full items-center justify-center rounded-lg p-2 text-sidebar-foreground transition-all duration-150 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="h-[18px] w-[18px] shrink-0 text-sidebar-foreground/80" /> : <Moon className="h-[18px] w-[18px] shrink-0 text-sidebar-foreground/80" />}
            <span className="pointer-events-none absolute left-full ml-2 z-50 hidden whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[12px] font-medium text-background shadow-md group-hover:block">
              {isDark ? 'Light mode' : 'Dark mode'}
            </span>
          </motion.button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={toggleTheme}
            className="mb-2 flex w-full items-center gap-3 rounded-lg border-l-2 border-transparent px-3 py-2 lg:py-2.5 text-[14px] lg:text-[15px] font-medium text-sidebar-foreground transition-all duration-150 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="h-4 w-4 lg:h-[18px] lg:w-[18px] shrink-0 text-sidebar-foreground/80" /> : <Moon className="h-4 w-4 lg:h-[18px] lg:w-[18px] shrink-0 text-sidebar-foreground/80" />}
            <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
          </motion.button>
        )}

        {/* User info + notifications + logout */}
        {user && (
          minimised ? (
            <div className="flex flex-col items-center gap-1.5">
              <NotificationBell />
              <motion.button
                whileTap={{ scale: 0.92 }}
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                title="Log out"
                className="group relative rounded-lg p-1.5 text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                aria-label="Log out"
              >
                <LogOut className="h-4 w-4" />
                <span className="pointer-events-none absolute left-full ml-2 z-50 hidden whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[12px] font-medium text-background shadow-md group-hover:block">
                  Log out
                </span>
              </motion.button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-lg px-1">
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <NotificationBell />
                <p className="truncate text-[14px] font-medium text-sidebar-foreground">{user.email}</p>
              </div>
              <div className="flex items-center gap-0.5">
                <motion.button
                  whileTap={{ scale: 0.92 }}
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="rounded-lg p-1.5 text-sidebar-foreground/60 transition-colors hover:bg-sidebar-accent hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                  aria-label="Log out"
                >
                  <LogOut className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )
        )}

        {/* Collapse / Expand toggle — desktop only */}
        <div className="mt-2 hidden lg:block">
          <div className="mb-2 h-px bg-sidebar-border/50" />
          <motion.button
            whileTap={{ scale: 0.92 }}
            onClick={onToggleMinimise}
            className={cn(
              'flex w-full items-center rounded-lg p-2 text-sidebar-foreground/70 transition-all duration-150 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              minimised ? 'justify-center' : 'gap-3 px-3',
            )}
            aria-label={minimised ? 'Expand sidebar' : 'Collapse sidebar'}
            title={minimised ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {minimised ? (
              <ChevronRight className="h-[18px] w-[18px] shrink-0" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 lg:h-[18px] lg:w-[18px] shrink-0" />
                <span className="text-[14px] lg:text-[15px] font-medium">Collapse</span>
              </>
            )}
          </motion.button>
        </div>
      </div>
    </aside>
  );
}
